export const APPROVAL_STATUS_POLL_INTERVAL_MS = 2000

/**
 * After the network approves, waiting rows are written asynchronously by the
 * review-distribution Lambda. Until they exist the PAC already knows *who*
 * belongs on the campaign (the curated list) but not *their status*.
 *
 * `pending_review` and network-level `rejected` are excluded: distribution
 * has not started, so polling would spin until a human approves.
 */
export const isAwaitingSubpublisherDistribution = (
  campaignStatus?: string | null
) =>
  !!campaignStatus &&
  campaignStatus !== 'pending_review' &&
  campaignStatus !== 'rejected'

type CuratedPollRow = {
  active?: boolean
  subpublisher_id: string
}

type ApprovalPollRow = {
  publisher_id: string
}

/**
 * Empty curated + empty approval: Lambda may still be materializing.
 * Empty curated + approval already present: "full network" with no
 * campaign_subpublishers backfill — stop, or we poll forever.
 */
export const shouldPollCuratedListForDistribution = ({
  awaitingDistribution,
  curatedCount,
  approvalCount
}: {
  awaitingDistribution: boolean
  curatedCount: number
  approvalCount: number
}) => awaitingDistribution && curatedCount === 0 && approvalCount === 0

export const shouldPollApprovalStatusForDistribution = ({
  awaitingDistribution,
  curatedRows,
  approvalPublishers
}: {
  awaitingDistribution: boolean
  curatedRows?: CuratedPollRow[] | null
  approvalPublishers?: ApprovalPollRow[] | null
}) => {
  if (!awaitingDistribution) return false

  const publishers = approvalPublishers ?? []
  const approvalIds = new Set(publishers.map(item => item.publisher_id))
  const activeCurated = (curatedRows ?? []).filter(row => row.active !== false)

  if (activeCurated.some(row => !approvalIds.has(row.subpublisher_id))) {
    return true
  }

  return activeCurated.length === 0 && approvalIds.size === 0
}

export const getApprovalStatusRefetchInterval = (hasPendingRows: boolean) =>
  hasPendingRows ? APPROVAL_STATUS_POLL_INTERVAL_MS : false
